/**
 * Formulario de tipo de préstamo (crear y editar): nombre, frecuencia, % opcional y días para clavo.
 * Si el tipo no tiene % fijo, el % se escribe al crear cada préstamo; las cuotas siempre se eligen
 * en el préstamo. El API valida y responde el campo que falla.
 */
import React, { SyntheticEvent, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import { Notice, PageHeader, useNotify } from '@routes-plan/ui';
import { AdminPage, FormActions, FormSection, FullRow } from '../shared/AdminUi';
import { TextKey, useT } from '../../../i18n';
import { setPageTitle } from '../../../utils/pageTitle';

interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

type Field = 'name' | 'loanTypeKindId' | 'interestRate' | 'defaultDays';
const FREQUENCIES = [1, 2, 3, 4];
const LIST = '/admin/loan-types';

const LoanTypeForm: React.FC<ShowAlerts & { id?: string }> = ({ id }) => {
  const t = useT();
  const notify = useNotify();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [frequency, setFrequency] = useState<number | ''>('');
  const [fixed, setFixed] = useState(true);
  const [interestRate, setInterestRate] = useState('');
  const [defaultDays, setDefaultDays] = useState('');
  const [term, setTerm] = useState(0);
  const [errors, setErrors] = useState<Partial<Record<Field, string>>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setPageTitle(id ? 'loanTypes.editTitle' : 'loanTypes.newTitle');
    if (!id) return;
    (async () => {
      try {
        const { data } = await axios.get(`loan-types/${id}`);
        setName(data.name ?? '');
        setFrequency(Number(data.loanTypeKind?.id) || '');
        const isFixed = Number(data.fixedInterest ?? 1) !== 0;
        setFixed(isFixed);
        setInterestRate(isFixed ? String(data.interestRate ?? '') : '');
        setDefaultDays(String(data.defaultDays ?? ''));
        setTerm(Number(data.term) || 0);
      } catch {
        notify.error(t('loanTypes.loadError'));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const validate = () => {
    const next: Partial<Record<Field, string>> = {};
    if (name.trim().length < 2) next.name = t('loanTypes.required');
    if (!frequency) next.loanTypeKindId = t('loanTypes.required');
    if (fixed && (interestRate === '' || !Number.isInteger(Number(interestRate)) || Number(interestRate) < 0)) next.interestRate = t('loanTypes.required');
    if (defaultDays === '' || !Number.isInteger(Number(defaultDays)) || Number(defaultDays) < 0) next.defaultDays = t('loanTypes.required');
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    if (saving || !validate()) return;
    const body = {
      name: name.trim(),
      loanTypeKindId: frequency,
      interestRate: fixed ? Number(interestRate) : null,
      defaultDays: Number(defaultDays),
      // Plazo en días de las constantes de Mis Rutas: se conserva al editar; los tipos nuevos no lo usan.
      term,
    };
    setSaving(true);
    try {
      if (id) await axios.put(`loan-types/${id}`, body);
      else await axios.post('loan-types', body);
      notify.success(t('loanTypes.saved'));
      navigate(LIST);
    } catch (error: any) {
      const data = error?.response?.data;
      if (data?.field) setErrors({ [data.field as Field]: data.message });
      notify.error(data?.message || t('loanTypes.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const title = t(id ? 'loanTypes.editTitle' : 'loanTypes.newTitle');
  return (
    <AdminPage narrow>
      <PageHeader
        title={title}
        description={t('loanTypes.formSubtitle')}
        breadcrumbs={[{ label: t('loanTypes.title'), href: `#${LIST}` }, { label: title }]}
      />
      <Box component="form" onSubmit={submit} noValidate sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <FormSection title={t('loanTypes.sectionData')}>
          <TextField
            id="loan-type-name"
            label={t('common.name')}
            data-tour="loan-type-name"
            placeholder={t('loanTypes.namePlaceholder')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            inputProps={{ maxLength: 255 }}
            error={Boolean(errors.name)}
            helperText={errors.name}
            required
            fullWidth
          />
          <TextField
            id="loan-type-frequency"
            select
            label={t('loanTypes.frequency')}
            data-tour="loan-type-frequency"
            value={frequency}
            onChange={(e) => setFrequency(Number(e.target.value))}
            error={Boolean(errors.loanTypeKindId)}
            helperText={errors.loanTypeKindId}
            required
            fullWidth
          >
            {FREQUENCIES.map((f) => (
              <MenuItem key={f} value={f}>
                {t(`frequency.${f}` as TextKey)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            id="loan-type-days"
            label={t('loanTypes.cloveday')}
            type="number"
            value={defaultDays}
            onChange={(e) => setDefaultDays(e.target.value)}
            inputProps={{ min: 0, step: 1, inputMode: 'numeric' }}
            error={Boolean(errors.defaultDays)}
            helperText={errors.defaultDays || t('loanTypes.clovedayHelp')}
            required
            fullWidth
          />
        </FormSection>
        <FormSection title={t('loanTypes.sectionUtility')} description={t('loanTypes.fixedUtilityHelp')}>
          <FullRow>
            <FormControlLabel
              control={
                <Switch
                  checked={fixed}
                  onChange={(e) => {
                    setFixed(e.target.checked);
                    setErrors((prev) => ({ ...prev, interestRate: undefined }));
                  }}
                />
              }
              label={t('loanTypes.fixedUtility')}
            />
          </FullRow>
          {fixed && (
            <TextField
              id="loan-type-utility"
              label={t('loanTypes.utility')}
              type="number"
              value={interestRate}
              onChange={(e) => setInterestRate(e.target.value)}
              inputProps={{ min: 0, max: 1000, step: 1, inputMode: 'numeric' }}
              InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
              error={Boolean(errors.interestRate)}
              helperText={errors.interestRate}
              required
              fullWidth
            />
          )}
        </FormSection>
        {id && <Notice severity="info">{t('loanTypes.editNote')}</Notice>}
        <FormActions cancelTo={LIST} saving={saving} />
      </Box>
    </AdminPage>
  );
};

export default LoanTypeForm;
